import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";

type OtpInputsProps = {
  values: string[];
  onChange: (values: string[]) => void;
  onClearError?: () => void;
  invalid?: boolean;
  autoSubmit?: boolean;
};

export function OtpInputs({ values, onChange, onClearError, invalid, autoSubmit = true }: OtpInputsProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const lastSent = useRef("");
  const code = values.join("");
  const complete = values.length > 0 && values.every((digit) => /^\d$/.test(digit));

  useEffect(() => {
    if (!complete) {
      lastSent.current = "";
      return;
    }
    if (!autoSubmit || lastSent.current === code) return;
    lastSent.current = code;
    refs.current[values.length - 1]?.blur();
    refs.current[0]?.form?.requestSubmit();
  }, [autoSubmit, code, complete, values.length]);

  const setDigit = (index: number, digit: string) => {
    const next = [...values];
    next[index] = digit;
    onChange(next);
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = (event.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    event.preventDefault();
    const next = Array.from({ length: 6 }, (_, index) => text[index] ?? "");
    onChange(next);
    refs.current[Math.min(text.length, 5)]?.focus();
    onClearError?.();
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !values[index] && refs.current[index - 1]) {
      refs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="otp" role="group" aria-label="6 digit code">
      {values.map((value, index) => (
        <input
          key={index}
          ref={(node) => {
            refs.current[index] = node;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${index + 1}`}
          aria-invalid={invalid || undefined}
          value={value}
          onChange={(event) => {
            const digit = event.target.value.replace(/\D/g, "").slice(-1);
            setDigit(index, digit);
            onClearError?.();
            if (digit && refs.current[index + 1]) refs.current[index + 1]?.focus();
          }}
          onKeyDown={(event) => onKeyDown(index, event)}
          onPaste={onPaste}
        />
      ))}
    </div>
  );
}

export function EyeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
